import React, { ReactNode, useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	ListItem,
	Stack,
	Typography,
	IconButton,
	Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";
import moment from "moment";
import { PhoneInput } from "../../../components/PhoneInput";

import DefaultTable from "../../../components/Table/DefaultTable";
import { Input } from "../../../components/Input";
import { CustomModal } from "../../../components/modal/CustomModal";
import { CustomDialog } from "../../../components/modal/CustomDialog";
import { usStates } from "../../../constants/Constant";
import { SelectComponent } from "../../../components/Select";
import {
	findDealersAndQuotesAll,
	inviteDealer,
	reinviteDealer,
	disableDealer,
	enableDealer,
	deleteDealer,
	getInviteStatuses,
} from "../../../apis/dealer";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { useCompanyData } from "../../../hook/useCompanyData";
import { Loader } from "../../../components/Loader";
import { checkActiveStatus } from "../../../utils/dealer";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface StatCardProps {
	value: number | string;
	description: string;
}

const StatCard: React.FC<StatCardProps> = ({ value, description }) => (
	<Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
		<Card sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}>
			<CardContent sx={{ pb: "10px !important" }}>
				<Typography variant="h5" component="div">
					{value}
				</Typography>
				<Typography sx={{ mb: 1.5 }} color="text.secondary">
					{description}
				</Typography>
			</CardContent>
		</Card>
	</Grid>
);

interface TRowData<T> {
	[key: string]: T;
}

const headerData: string[] = [
	`${process.env.REACT_APP_DEALER_FULL_NAME}`,
	"Contact",
	"Quotes",
	"Last Activity",
	"Status",
	"Actions",
];

const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

const getThirtyDaysAgoNumber = (data: any) => {
	const all = data.filter(
		(item: { createdAt: string }) => new Date(item.createdAt) > thirtyDaysAgo
	)?.length;
	const activities = data.filter(
		(item: { createdAt: string; status: string }) =>
			new Date(item.createdAt) > thirtyDaysAgo && item.status === "Activity"
	)?.length;
	return [all, activities];
};

const getEmailEventIndicator = (lastEvent: string): { label: string; color: string; tooltip: string } | null => {
	switch (lastEvent) {
		case 'delivered':
			return { label: 'Email Delivered', color: 'success', tooltip: 'Invitation email was delivered to their inbox' };
		case 'opened':
			return { label: 'Email Opened', color: 'info', tooltip: 'They opened the invitation email' };
		case 'clicked':
			return { label: 'Link Clicked', color: 'primary', tooltip: 'They clicked the invitation link' };
		case 'bounced':
			return { label: 'Email Bounced', color: 'error', tooltip: 'Email could not be delivered — check the email address' };
		case 'complained':
			return { label: 'Marked Spam', color: 'error', tooltip: 'Recipient marked the email as spam' };
		case 'sent':
			return { label: 'Email Sent', color: 'default', tooltip: 'Invitation email was sent, waiting for delivery confirmation' };
		case 'delayed':
			return { label: 'Email Delayed', color: 'warning', tooltip: 'Email delivery is delayed' };
		default:
			return null;
	}
};

export const Dealers: React.FC = () => {
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [companyError, setCompanyError] = useState(true);
	const [contactError, setContactError] = useState(true);
	const [emailError, setEmailError] = useState(true);
	const [dealers, setDealers] = useState<TRowData<ReactNode>[]>([]);
	const [dealersData, setDealersData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState<boolean | false>(true);
	const [thirtyDaysAgoNumbers, setThirtyDaysAgoNumbers] = useState<number[]>([
		0, 0,
	]);
	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(20);
	const [count, setCount] = useState<number>(0);
	const [openReinviteModal, setOpenReinviteModal] = useState<boolean>(false);
	const [openDisableModal, setOpenDisableModal] = useState<boolean>(false);
	const [openEnableModal, setOpenEnableModal] = useState<boolean>(false);
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const [selectedDealer, setSelectedDealer] = useState<any | null>(null);
	const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
	const [inviteStatuses, setInviteStatuses] = useState<Record<string, { lastEvent: string; timeline: any[] }>>({});

	const { company } = useCompanyData();

	const { showSnackBar } = useGlobalSnackBar();
	const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

		const fetchDealers = async () => {
		try {
			const [dealerResponse, statusResponse] = await Promise.all([
				findDealersAndQuotesAll({ page: page + 1, pageSize }),
				getInviteStatuses().catch(() => ({})),
			]);
			const { data: dealerData, counts } = dealerResponse;
			const statuses: Record<string, { lastEvent: string; timeline: any[] }> = statusResponse || {};
			setInviteStatuses(statuses);
			setDealersData(dealerData);

			const transformedData = dealerData.map((dealer: any) => {
				const status = checkActiveStatus(
					dealer.created_at,
					company?.active_dealer_setting_number +
					company?.active_dealer_setting_date,
					dealer
				);

				// Email event indicator for invite statuses
				const emailKey = dealer.email?.toLowerCase();
				const inviteStatus = emailKey ? statuses[emailKey] : null;
				const emailIndicator = inviteStatus && inviteStatus.lastEvent !== 'none'
					? getEmailEventIndicator(inviteStatus.lastEvent)
					: null;

				return {
					dealer: (
						<ListItem
							disablePadding
							component={Link}
							to={`/dealers/${dealer.ID}`}
						>
							{dealer.dealer_name || "Undefined"}
						</ListItem>
					),
					contact: dealer.contact_name,
					quotes: dealer.quote_number,
					last_activity:
						dealer.last_activity_date &&
						moment(dealer.last_activity_date).format("MM/DD/YYYY"),
					status: (
						<Stack direction="column" spacing={0.25}>
							<span>{status}</span>
							{(status === "Invite Sent" || status === "Invite Expired") && dealer.invitation_sent_at && (
								<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
									Invited {moment(dealer.invitation_sent_at).format("MMM D, YYYY")}
								</Typography>
							)}
							{emailIndicator && (status === "Invite Sent" || status === "Invite Expired") && (
								<Tooltip title={emailIndicator.tooltip}>
									<Chip
										label={emailIndicator.label}
										size="small"
										color={emailIndicator.color as any}
										variant="outlined"
										sx={{ height: 20, fontSize: '0.65rem', width: 'fit-content' }}
									/>
								</Tooltip>
							)}
						</Stack>
					),
					actions: (
						<Stack direction="row" spacing={1}>
							{(status === "Invite Sent" || status === "Invite Expired") && (
								<Tooltip title="Reinvite">
									<IconButton
										size="small"
										color="primary"
										onClick={() => handleReinvite(dealer)}
									>
										<EmailIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							{(status === "Active" || status === "Inactive") && dealer.password && (
								<Tooltip title="Disable">
									<IconButton
										size="small"
										color="warning"
										onClick={() => handleDisable(dealer.ID)}
									>
										<BlockIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							{status === "Disabled" && (
								<Tooltip title="Enable">
									<IconButton
										size="small"
										color="success"
										onClick={() => handleEnable(dealer.ID)}
									>
										<CheckCircleIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							<Tooltip title="Delete">
								<IconButton
									size="small"
									color="error"
									onClick={() => handleDelete(dealer.ID)}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
					),
				};
			});

			setDealers(transformedData);
			setCount(counts);

			setThirtyDaysAgoNumbers(getThirtyDaysAgoNumber(dealerData));
			setIsLoading(false);
		} catch (error) {
			showSnackBar("Failed to fetch dealers", "error");
		}
	};

	useEffect(() => {
		setIsLoading(true);
		if (company) {
			fetchDealers();
		}
	}, [page, pageSize, company]);

	const permissionErrorMessage = () => {
		showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
	};

	const handleSubmit = async (e: any) => {
		e.preventDefault();

		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}

		if (companyError || contactError || emailError) {
			showSnackBar("Form is invalid! Please check the fields...", "error");
		} else {
			const formData = new FormData(e.target);
			const formValues: any = Object.fromEntries(formData.entries());

			for (const key in formValues) {
				if (formValues[key] === "") {
					delete formValues[key];
				}
			}

			try {
				await inviteDealer(formValues);
				showSnackBar("Invitation Sent!");
				setOpenModal(false);
				fetchDealers();
			} catch (err) {
				showSnackBar("Server error.", "error");
			}
		}
	};

	const handleReinvite = (dealer: any) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}
		setSelectedDealer(dealer);
		setOpenReinviteModal(true);
	};

	const onReinvite = async () => {
		try {
			await reinviteDealer({
				ID: selectedDealer?.ID,
				message: company?.invite_message_description || "You've been invited to join the BigFoot Mobile Systems dealer portal.",
			});
			showSnackBar("Reinvitation sent!");
			fetchDealers();
			setOpenReinviteModal(false);
			setSelectedDealer(null);
		} catch (err) {
			showSnackBar("Failed to send reinvitation.", "error");
		}
	};

	const handleDisable = (id: string) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}
		setSelectedDealerId(id);
		setOpenDisableModal(true);
	};

	const onDisable = async () => {
		try {
			await disableDealer({ ID: selectedDealerId || "" });
			showSnackBar("Dealer disabled successfully");
			fetchDealers();
			setOpenDisableModal(false);
			setSelectedDealerId(null);
		} catch (err) {
			showSnackBar("Failed to disable dealer.", "error");
		}
	};

	const handleEnable = (id: string) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}
		setSelectedDealerId(id);
		setOpenEnableModal(true);
	};

	const onEnable = async () => {
		try {
			await enableDealer({ ID: selectedDealerId || "" });
			showSnackBar("Dealer enabled successfully");
			fetchDealers();
			setOpenEnableModal(false);
			setSelectedDealerId(null);
		} catch (err) {
			showSnackBar("Failed to enable dealer.", "error");
		}
	};

	const handleDelete = (id: string) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}
		setSelectedDealerId(id);
		setOpenDeleteModal(true);
	};

	const onDelete = async () => {
		try {
			await deleteDealer({ ID: selectedDealerId || "" });
			showSnackBar("Dealer deleted successfully");
			fetchDealers();
			setOpenDeleteModal(false);
			setSelectedDealerId(null);
		} catch (err) {
			showSnackBar("Failed to delete dealer.", "error");
		}
	};

	const handleCompanyChange = (e: any) => {
		if (e.target.value.length < 1) {
			setCompanyError(true);
		} else {
			setCompanyError(false);
		}
	};

	const handleContactChange = (e: any) => {
		if (e.target.value.length < 1) {
			setContactError(true);
		} else {
			setContactError(false);
		}
	};

	const handleEmailChange = (e: any) => {
		if (!/^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(e.target.value)) {
			setEmailError(true);
		} else {
			setEmailError(false);
		}
	};

	const handleChangePage = (page: number) => {
		setPage(page);
	};

	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setPageSize(Number(e.target.value));
	};

	if (isLoading) return <Loader isLoading={isLoading} />;

	return (
		<>
			<Box sx={{ flexGrow: 1 }}>
				<Grid container spacing={2}>
					<StatCard
						value={thirtyDaysAgoNumbers[0]}
						description={`New ${process.env.REACT_APP_DEALER_FULL_NAME}s (last 30 days)`}
					/>
					<StatCard
						value={thirtyDaysAgoNumbers[1]}
						description={`Active ${process.env.REACT_APP_DEALER_FULL_NAME}s (activity in the last 30 days)`}
					/>
					<StatCard value={count} description={`Total ${process.env.REACT_APP_DEALER_FULL_NAME}s`} />
					<Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
						<Card
							style={{ textAlign: "center", cursor: "pointer" }}
							onClick={() => setOpenModal(true)}
							sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}
						>
							<CardContent sx={{ pb: "10px !important" }}>
								<Typography variant="h5" component="div">
									+
								</Typography>
								<Typography sx={{ mb: 1.5 }} color="text.secondary">
									{`Invite ${process.env.REACT_APP_DEALER_FULL_NAME}`}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
			<Box sx={{ flexGrow: 1, mt: 3 }}>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<Input label="Search:" />
					</Grid>
					<Grid item xs={6}>
						<Stack spacing={2} direction="row">
							<Button variant="contained">Apply</Button>
							<Button variant="outlined">Reset</Button>
						</Stack>
					</Grid>
				</Grid>
			</Box>
			<Box sx={{ flexGrow: 1 }}>
				<Typography variant="h5" noWrap align="center" sx={{ mb: 2, mt: 3 }}>
					Recent Activity
				</Typography>
				<DefaultTable
					headerData={headerData}
					rowsData={dealers}
					rowsPerPage={pageSize}
					page={page}
					count={count}
					handleChangePage={(
						e: React.ChangeEvent<HTMLInputElement>,
						page: number
					) => handleChangePage(page)}
					handleChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChangeRowsPerPage(e)
					}
				/>
			</Box>
			<CustomModal
				openModal={openModal}
				handleClose={() => setOpenModal(false)}
				width={500}
				handleSubmit={handleSubmit}
			>
				<Typography variant="h5" noWrap align="center" sx={{ mb: 2 }}>
					{`Invite a ${process.env.REACT_APP_DEALER_FULL_NAME}`}
				</Typography>
				<Input
					label="Company Name"
					name="dealer_name"
					required
					error={companyError}
					onChange={handleCompanyChange}
					sx={{ mb: 2 }}
				/>
				<Input
					label="Contact"
					name="contact_name"
					required
					error={contactError}
					onChange={handleContactChange}
					sx={{ mb: 2 }}
				/>
				<Input
					label="E-mail Address"
					name="email"
					required
					error={emailError}
					onChange={handleEmailChange}
					sx={{ mb: 2 }}
				/>
				<PhoneInput label="Phone" name="phone" size="small" fullWidth sx={{ mb: 2 }} />
				<Input label="Address" name="address" sx={{ mb: 2 }} />
				<Input label="City" name="city" sx={{ mb: 2 }} />
				<SelectComponent label="State" name="state" menuItems={usStates} />
				<Input label="Zip" name="zip" sx={{ mb: 2, mt: 2 }} />
				<Input
					label="Message"
					name="message"
					sx={{ mb: 2, mt: 1 }}
					rows={5}
					defaultValue={company?.invite_message_description}
				/>
				<Typography align="center">
					<Button variant="contained" color="primary" type="submit">
						Send Invitation
					</Button>
				</Typography>
			</CustomModal>
			<CustomDialog
				title="Are you sure you want to resend the invitation to this user?"
				openModal={openReinviteModal}
				handleClose={() => {
					setOpenReinviteModal(false);
					setSelectedDealer(null);
				}}
				handleAction={onReinvite}
				buttonTitle="Yes"
				children={undefined}
			/>
			<CustomDialog
				title="Are you sure you want to disable this user?"
				openModal={openDisableModal}
				handleClose={() => {
					setOpenDisableModal(false);
					setSelectedDealerId(null);
				}}
				handleAction={onDisable}
				buttonTitle="Yes"
				children={undefined}
			/>
			<CustomDialog
				title="Are you sure you want to enable this user?"
				openModal={openEnableModal}
				handleClose={() => {
					setOpenEnableModal(false);
					setSelectedDealerId(null);
				}}
				handleAction={onEnable}
				buttonTitle="Yes"
				children={undefined}
			/>
			<CustomDialog
				title="Are you sure you want to delete this user?"
				openModal={openDeleteModal}
				handleClose={() => {
					setOpenDeleteModal(false);
					setSelectedDealerId(null);
				}}
				handleAction={onDelete}
				buttonTitle="Yes"
				children={undefined}
			/>
		</>
	);
};
